import {
  CDK_DRAG_CONFIG,
  CdkDragDrop,
  DragRefConfig,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { ImmoTask } from 'src/app/model/immo-task/immo-task';
import Employee from 'src/app/model/product/employee';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import { EmployeeTourEditInputStoreService } from 'src/app/services/employee-tour-edit/employee-tour-edit-input-store.service';
import { MapsService } from 'src/app/services/google-maps/maps.service';
import { GeocoderService } from 'src/app/services/geocoder.service';
import AddressHelper from 'src/app/helpers/address-helper';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { SnackService } from 'src/app/services/snack.service';
import { CreateEditTaskModalComponent } from '../../../task/create-edit-task/create-edit-task-modal.component';
import { AssignImmoTaskToTourControllerRequestService } from '../service/assign-immo-task-to-tour-controller-request.service';
import { AssignSubProjectToTourControllerRequestService } from '../service/assign-sub-project-to-tour-controller-request.service';
import { TourPlanningEventService } from '../service/tour-planning-event.service';
import { EmployeeTourEditModalHelper } from './helper/employee-tour-edit-modal-helper';
import { TourDetailImmoTask } from './helper/model/tour-detail-immo-task';
import { TourDetailSubProject } from './helper/model/tour-detail-sub-project';
import { TourDetailTour } from './helper/model/tour-detail-tour';
import { TourDetailTourItem } from './helper/model/tour-detail-tour-item';
import { ReorderTourItemsControllerRequestService } from './service/reorder-tour-items-controller-request.service';
import { ToggleRouteCheckedControllerRequestService } from './service/toggle-route-checked-controller-request.service';
import { TourDetailServiceControllerRequestService } from './service/tour-detail-controller-request.service';
import { TransferSubProjectsAndImmoTasksControllerRequestService } from './service/transfer-sub-projects-and-immo-tasks-controller-request.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'employee-tour-edit-modal',
  templateUrl: './employee-tour-edit-modal.component.html',
  styleUrls: ['./employee-tour-edit-modal.component.scss'],
  providers: [
    {
      provide: CDK_DRAG_CONFIG,
      useValue: EmployeeTourEditModalComponent.DRAG_CONFIG,
    },
    FormatPhoneNumber,
    PrintAddressPipe,
  ],
})
export class EmployeeTourEditModalComponent implements OnInit {
  @Input()
  placer: Employee;
  @Input()
  tourId: number;
  @Input()
  tourDate: moment.Moment;
  @Input()
  placerOptions: Employee[];
  @Input()
  hideRepos: boolean = false;

  @ViewChild('table') table: MatTable<TourDetailTourItem>;
  displayedColumns: string[];

  tour: TourDetailTour;
  loading: boolean = true;
  editingTour: boolean = false;

  tourItemsForMap: TourDetailTourItem[];

  selectedDate: moment.Moment;
  selectedPlacer: Employee;
  today = new Date();

  tourItemSelectedCount: number = 0;

  static readonly DRAG_CONFIG: DragRefConfig = {
    dragStartThreshold: 10,
    pointerDirectionChangeThreshold: 10,
    zIndex: 1041,
  };
  selectToggle: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<EmployeeTourEditModalComponent>,
    private tourDetailServiceControllerRequestService: TourDetailServiceControllerRequestService,
    private assignSubProjectToTourControllerRequestService: AssignSubProjectToTourControllerRequestService,
    private assignImmoTaskToTourControllerRequestService: AssignImmoTaskToTourControllerRequestService,
    private transferSubProjectsAndImmoTasksControllerRequestService: TransferSubProjectsAndImmoTasksControllerRequestService,
    private toggleRouteCheckedControllerRequestService: ToggleRouteCheckedControllerRequestService,
    private reorderTourItemsControllerRequestService: ReorderTourItemsControllerRequestService,
    private tourPlanningEventService: TourPlanningEventService,
    private employeeTourEditInputStoreService: EmployeeTourEditInputStoreService,
    private snackService: SnackService,
    private modalService: ModalService,
    private router: Router,
    private formatPhoneNumber: FormatPhoneNumber,
    private printAddressPipe: PrintAddressPipe,
    private mapsService: MapsService,
    private geocoderService: GeocoderService
  ) {}

  ngOnInit(): void {
    this.displayedColumns = [
      'index',
      'projectId',
      'currentCreatedAt',
      'requested',
      'company',
      'address',
      'type',
      'action',
      'edit',
      'other',
      'X',
    ];
    this.fetchTour();
    this.selectedDate = this.tourDate;
  }

  private async fetchTour(): Promise<void> {
    try {
      this.tour =
        await this.tourDetailServiceControllerRequestService.tourDetail(
          this.tourId,
          this.hideRepos
        );
      EmployeeTourEditModalHelper.processTour(
        this.tour,
        this.formatPhoneNumber,
        this.printAddressPipe
      );
      this.tourItemsForMap =
        EmployeeTourEditModalHelper.calculateTourItemsForMap(this.tour);
    } finally {
      this.loading = false;
    }
  }

  openSubProject(tourItem: TourDetailTourItem): void {
    if (tourItem.subProject == null || tourItem.subProject.project == null) {
      return;
    }
    try {
      this.employeeTourEditInputStoreService.storeEmployeeTourEditInput(
        tourItem.subProject.id,
        {
          placer: this.placer,
          tourId: this.tourId,
          tourDate: this.tourDate,
          placerOptions: this.placerOptions,
        }
      );
    } catch (err) {
      if (err instanceof Error) {
        this.snackService.showError(err.message);
      } else {
        this.snackService.showError(err);
      }
    }
    this.router.navigate([
      '/job',
      tourItem.subProject.project.id,
      tourItem.subProject.id,
      true,
    ]);
    this.closeIfDialog(true);
  }

  openTaskEditModal(task: ImmoTask) {
    this.modalService.openComponentInModal(
      CreateEditTaskModalComponent,
      [
        {
          key: 'taskId',
          value: task.id,
        },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.result,
        (result) => {
          if (result === 'taskCreatedOrEdited' || result === 'taskArchived') {
            this.loading = true;
            this.fetchTour();
            this.tourPlanningEventService
              .getTourChangedForPlacersSubject()
              .next([
                { placerId: this.placer.id, date: this.tourDate.toDate() },
              ]);
          }
        }
      ),
      { minWidth: '97vw', height: '95vh' }
    );
  }

  async optimizeRoute(): Promise<void> {
    if (this.selectedDate == null) {
      this.snackService.showError('Gelieve een datum te kiezen');
      return;
    }

    try {
      this.editingTour = true;

      const placerCoordinates = await this.geocoderService.geocodeAddress(
        AddressHelper.getAddressDisplay(this.placer.address)
      );

      const order = await this.mapsService
        .optimizeRoute({
          tourItemsForMap: this.tourItemsForMap,
          date: this.selectedDate.toDate(),
          startLocation: placerCoordinates,
          loadingLocation: placerCoordinates, // TODO: set to correct location for loading
        })
        .toPromise();

      const tourItemIdToSequenceNumber = new Map(
        order.map((id, idx) => [parseInt(id), idx])
      );

      this.tour.tourItems.sort(
        this.sortItemsToSequenceOrder(tourItemIdToSequenceNumber)
      );

      await this.reorderTourItemsControllerRequestService.reorderTourItems(
        this.tour.id,
        tourItemIdToSequenceNumber
      );

      this.tourItemsForMap =
        EmployeeTourEditModalHelper.calculateTourItemsForMap(this.tour);
      this.tourPlanningEventService.getTourChangedForPlacersSubject().next([
        {
          placerId: this.placer.id,
          date: this.tourDate.toDate(),
        },
      ]);
      this.table.renderRows();
      this.snackService.showSuccess('Volgorde succesvol aangepast');
    } catch (error) {
      console.error(error);

      let message = 'Gefaald om de route te optimaliseren';
      if (error instanceof HttpErrorResponse && error.error?.message) {
        message = error.error?.message;
      }

      this.snackService.showError(message);
    } finally {
      this.editingTour = false;
    }
  }

  private sortItemsToSequenceOrder(
    tourItemIdToSequenceNumber: Map<number, number>
  ): (a: TourDetailTourItem, b: TourDetailTourItem) => number {
    return (a, b) => {
      const idA = this.getIdFromTourItem(a);
      const idB = this.getIdFromTourItem(b);
      const seqA = tourItemIdToSequenceNumber.get(idA);
      const seqB = tourItemIdToSequenceNumber.get(idB);

      if (seqA === undefined && seqB === undefined) return 0;
      if (seqA === undefined) return 1;
      if (seqB === undefined) return -1;

      return seqA - seqB;
    };
  }

  openCreateEditTaskModal(): void {
    this.modalService.openComponentInModal(
      CreateEditTaskModalComponent,
      [
        {
          key: 'placerInput',
          value: this.placer,
        },
        {
          key: 'tourDate',
          value: this.tourDate,
        },
        {
          key: 'focusSubjectField',
          value: true,
        },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.result,
        (result) => {
          if (result === 'taskCreatedOrEdited') {
            this.loading = true;
            this.fetchTour();
            this.tourPlanningEventService
              .getTourChangedForPlacersSubject()
              .next([
                { placerId: this.placer.id, date: this.tourDate.toDate() },
              ]);
          }
        }
      ),
      { minWidth: '97vw', height: '95vh' }
    );
  }

  routeCheckedChanged(): void {
    this.editingTour = true;
    const oldValue = !this.tour.routeChecked;
    this.toggleRouteCheckedControllerRequestService
      .toggleRouteChecked(this.tour.id)
      .then(() => {
        this.snackService.showSuccess(
          `Routeplanning intern opgemaakt succesvol ${
            this.tour.routeChecked ? 'aangevinkt' : 'uitgevinkt'
          }`
        );
        this.tourPlanningEventService
          .getTourChangedForPlacersSubject()
          .next([{ placerId: this.placer.id, date: this.tourDate.toDate() }]);
      })
      .catch(() => (this.tour.routeChecked = oldValue))
      .finally(() => (this.editingTour = false));
  }

  removeItemFromTour(tourItem: TourDetailTourItem): void {
    if (tourItem.locked) {
      return;
    }
    if (tourItem.subProject != null) {
      this.removeSubProjectFromTour(tourItem.subProject);
    } else if (tourItem.immoTask != null) {
      this.removeImmoTaskFromTour(tourItem.immoTask);
    }
  }

  private removeSubProjectFromTour(subProject: TourDetailSubProject): void {
    const idPart =
      subProject == null || subProject.project == null
        ? ''
        : subProject.project.id;
    this.modalService.confirmDialog(
      `Opdracht #${idPart} verwijderen uit toer`,
      `Bent u zeker dat u opdracht #${idPart} wilt verwijderen uit deze toer?`,
      (b) => {
        if (b) {
          this.loading = true;
          this.doRemoveSubProjectFromTour(subProject);
        }
      }
    );
  }

  private async doRemoveSubProjectFromTour(
    subProject: TourDetailSubProject
  ): Promise<void> {
    try {
      await this.assignSubProjectToTourControllerRequestService.assignSubProjectToTour(
        subProject.id
      );
      this.snackService.showSuccess('Opdracht succesvol verwijderd uit toer');
      this.tourPlanningEventService.getTourChangedForPlacersSubject().next([
        {
          placerId: this.placer.id,
          date: this.tourDate.toDate(),
        },
      ]);
      this.tourPlanningEventService.getSubProjectWasUnassignedSubject().next();
      this.fetchTour();
    } catch {
      this.loading = false;
    }
  }

  private removeImmoTaskFromTour(immoTask: TourDetailImmoTask): void {
    this.modalService.confirmDialog(
      `Taak #${immoTask.id} verwijderen uit toer`,
      `Bent u zeker dat u taak #${immoTask.id} wilt verwijderen uit deze toer?`,
      (b: boolean) => {
        if (b) {
          this.loading = true;
          this.doRemoveImmoTaskFromTour(immoTask);
        }
      }
    );
  }

  private async doRemoveImmoTaskFromTour(
    immoTask: TourDetailImmoTask
  ): Promise<void> {
    try {
      await this.assignImmoTaskToTourControllerRequestService.assignImmoTaskToTour(
        immoTask.id
      );
      this.snackService.showSuccess('Taak succesvol verwijderd uit toer');
      this.tourPlanningEventService.getTourChangedForPlacersSubject().next([
        {
          placerId: this.placer.id,
          date: this.tourDate.toDate(),
        },
      ]);
      this.fetchTour();
    } catch {
      this.loading = false;
    }
  }

  tourItemSelectedChanged(value: boolean): void {
    this.tourItemSelectedCount += value ? 1 : -1;
  }

  private getIdFromTourItem(item: TourDetailTourItem): number | undefined {
    if (item.immoTask) {
      return item.immoTask.id;
    }

    if (item.subProject) {
      return item.subProject.project.id;
    }

    return undefined;
  }

  private assertFieldsNotEmpty(): string | null {
    if (this.selectedDate == null) {
      return 'Gelieve een datum te kiezen';
    }

    if (this.selectedPlacer == null) {
      return 'Gelieve een installateur te kiezen';
    }

    return null;
  }

  transferSelectedSubProjects(): void {
    let error = this.assertFieldsNotEmpty();
    if (this.tourItemSelectedCount < 1) {
      error = 'Gelieve minstens één opdracht te selecteren';
    }
    if (error != null) {
      this.snackService.showError(error);
      return;
    }
    this.doTransferSelectedSubProjects();
  }

  private async doTransferSelectedSubProjects(): Promise<void> {
    this.loading = true;
    const selectedSubProjectIds = [];
    const selectedImmoTaskIds = [];
    this.tour.tourItems.forEach((ti) => {
      if (ti.selected) {
        if (ti.subProject != null) {
          selectedSubProjectIds.push(ti.subProject.id);
        } else {
          selectedImmoTaskIds.push(ti.immoTask.id);
        }
      }
    });
    const selectedDate = this.selectedDate.toDate();
    try {
      await this.transferSubProjectsAndImmoTasksControllerRequestService.transferSubProjectsAndImmoTasksToOtherTour(
        this.tour.id,
        selectedSubProjectIds,
        selectedImmoTaskIds,
        this.selectedPlacer.id,
        selectedDate
      );
      this.tourPlanningEventService.getTourChangedForPlacersSubject().next([
        {
          placerId: this.placer.id,
          date: this.tourDate.toDate(),
        },
        {
          placerId: this.selectedPlacer.id,
          date: selectedDate,
        },
      ]);
      this.tourItemSelectedCount = 0;
      this.snackService.showSuccess(
        'Opdrachten succesvol verplaatst naar andere toer'
      );
    } catch {
      this.loading = false;
    }
    this.fetchTour();
  }

  private closeIfDialog(intendOpenSubProject?: boolean): void {
    if (this.dialogRef != null) {
      this.dialogRef.close(intendOpenSubProject);
    }
  }

  onListDrop(event: CdkDragDrop<TourDetailTourItem[]>): void {
    this.editingTour = true;
    const prevIndex = this.tour.tourItems.findIndex(
      (ti) => ti.id === event.item.data.id
    );
    moveItemInArray(this.tour.tourItems, prevIndex, event.currentIndex);
    this.table.renderRows();
    const tourItemIdToSequenceNumber = new Map<number, number>();
    this.tour.tourItems.forEach((ti, i) => {
      ti.sequenceNumber = i;
      tourItemIdToSequenceNumber.set(ti.id, i);
    });
    this.reorderTourItemsControllerRequestService
      .reorderTourItems(this.tour.id, tourItemIdToSequenceNumber)
      .then(() => {
        this.tourItemsForMap =
          EmployeeTourEditModalHelper.calculateTourItemsForMap(this.tour);
        this.tourPlanningEventService.getTourChangedForPlacersSubject().next([
          {
            placerId: this.placer.id,
            date: this.tourDate.toDate(),
          },
        ]);
        this.snackService.showSuccess('Volgorde succesvol aangepast');
      })
      .finally(() => (this.editingTour = false));
  }

  close() {
    this.closeIfDialog();
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }

  toggleAll() {
    this.selectToggle = !this.selectToggle;
    this.tour.tourItems.forEach((ti) => {
      ti.selected = this.selectToggle;
    });
    this.tourItemSelectedCount = this.selectToggle
      ? this.tour.tourItems.length
      : 0;
  }
}
